import $ from 'jquery'
import _, { random } from 'lodash'
import { JSONEditor } from '@json-editor/json-editor'
import store from '../../store'
import EventBus from '../../helpers/event-bus'
import Draggable from 'gsap/Draggable'
import gsap from 'gsap'
gsap.registerPlugin(Draggable);

class AssetEditor extends JSONEditor.AbstractEditor {
  constructor (...args) {
    super(...args)
    this.parentImgLoaded = false
    this.draggables = {}
    this.naturalSize = false
    this.uuid = self.crypto.randomUUID ? self.crypto.randomUUID() : self.crypto.getRandomValues(new Uint32Array(4)).join('-')  // fallback for older browsers, requires https
    this.hasWidth = false
    this.isResizing = false
    this.isDragging = false
    this.isClicked = false
    this._keyUp = null
    this._keyDown = null
    this.hotspotInstance = 0
    const pathRegex = /^root\.blocks\.(\d+)/
    const match = this.options?.path?.match(pathRegex)
    if (match) {
      this.hotspotInstance = parseInt(match[1], 10) // Extract and set the number
    }
    this.isHotspot = this.parent.schema?.options?.isHotspotGraphic || false
  }

  build () {
    this.title = this.header = this.label = this.theme.getFormInputLabel(this.getTitle())
    this.input = this.theme.getFormInputField('hidden')

    const label = _.startCase(this.schema.options.assetButtonLabel || this.schema.options.assetType)
    this.button = this.theme.getButton(`Add ${label}`, null, `Add ${label}`, 'btn-primary')

    // clear button
    this.clear = this.theme.getButton(`Remove ${label}`, null, `Remove ${label}`, 'btn-outline-danger')
    this.clear.style.minWidth = 'auto'

    this.replace = this.theme.getButton(`Select New ${label}`, null, `Select New ${label}`)
    this.replace.style.minWidth = 'auto'


    // set button initial states
    $(this.clear).toggleClass('d-none', !this.value)
    $(this.replace).toggleClass('d-none', !this.value)
    $(this.button).toggleClass('d-none', !!this.value)

    this.button.onclick = this.replace.onclick = async (event) => {
      // console.info('Triggering Asset Manager', this.schema.options.assetType)

      // pass schema options to the asset manager
      store.commit('assets/setOptions', this.schema.options)

      var $manager = $('#asset-manager-modal')
      $manager.one('selected', (event, { file }) => {
        store.commit('assets/setSelectedId', null)
        this.setValue(file.url)
      })

      // when modal is closed stop listening
      $manager.on('hide.bs.modal', () => {
        $manager.off('selected')
      })

      $manager.modal('show')

      if (!store.state.assets.loaded) {
        await store.dispatch('assets/update')
      }

      store.dispatch('assets/selectByFilename', this.value)
    }

    this.clear.onclick = (event) => {
      this.setValue(null)
    }

    this.preview = document.createElement('div')
    this.preview.classList.add('preview')
    this.preview.classList.add('fade')

    this.control = document.createElement('div')
    this.control.classList.add('d-flex')
    this.control.classList.add('flex-column')
    this.control.classList.add('align-items-start')

    this.buttons = document.createElement('div')
    this.buttons.classList.add('d-flex')
    this.buttons.classList.add('asset-buttons')
    this.buttons.appendChild(this.replace)
    this.buttons.appendChild(this.clear)

    // check for description
    if (_.has(this.schema, 'description')) {
      this.description = this.theme.getFormInputDescription(_.get(this.schema, 'description'))
      this.control.appendChild(this.description)
    }

    this.control.appendChild(this.input)
    this.control.appendChild(this.button)
    this.control.appendChild(this.preview)
    this.control.appendChild(this.buttons)

    // check for preview
    this.refreshPreview()

    EventBus.$on('assets:updated', this.refreshPreview)

    this.container.appendChild(this.control)
  }

  postBuild () {
    super.postBuild()
    if (this.isHotspot) {

      // because postBuild is called multiple times by the editor, need to remove these before adding them to unsure they are only added once
      EventBus.$off(`hotspot:added${this.uuid}`)
      EventBus.$off(`hotspot:changed${this.uuid}`)
      EventBus.$off(`hotspot:removed${this.uuid}`)
      EventBus.$off(`hotspot:newActiveTab${this.uuid}`)

      EventBus.$on(`hotspot:added${this.uuid}`, function (row, instanceHotspot) {
        this.addHotspotNode(row, instanceHotspot)
      }.bind(this))
      EventBus.$on(`hotspot:newActiveTab${this.uuid}`, () => { this.onResize(this.uuid, true) })
      EventBus.$on(`hotspot:changed${this.uuid}`, _.debounce(this.validateDifference.bind(this), 100))
      EventBus.$on(`hotspot:removed${this.uuid}`, (row, uuid) => { this.removeHotspotNode(row, uuid) })
      window.addEventListener('resize', () => { this.onResize(this.uuid) })
      this.addKeyListeners();
      this.hotspotInstance++
    }
  }

  async refreshPreview () {
    if (!this)
      return

    this.parentImgLoaded = false
    if (this.value) {
      let el
      let img
      // add preview for authoring tool
      switch (this.schema.options.assetType) {
        case 'image':
          el = document.createElement('div')
          el.id = 'asset-graphic-wrapper'
          img = document.createElement('img')
          img.id = 'asset-preview'
          img.classList.add('mt-0')
          img.src = this.value
          el.appendChild(img)
          break
        case 'audio':
          el = document.createElement('audio')
          el.controls = true
          el.src = this.value
          break
        case 'video':
          el = document.createElement('video')
          el.controls = true
          el.src = this.value
          break
        case 'document':
          el = this.theme.getButton('View Document', null, 'View Document', 'btn-dark')
          el.style.minWidth = 'auto'
          el.onclick = (event) => {
            window.open(this.value)
          }
          break
        default:
          break
      }

      if (el) {
        el.title = this.value
        this.preview.innerHTML = ''
        this.preview.appendChild(el)
        _.defer(() => {
          this.preview.classList.add('show')
        })
        if (this.parent?.parent?.value?.nodes && this.parent?.parent?.value?.nodes.length > 0 && !this.hasWidth) {
          this.hasWidth = this.parent.parent.value?.nodes[0]?.position?.width !== undefined
        }
        if (img) {
          img.onload = async () => {
            if (!this.parentImgLoaded) {
              if (this.isHotspot) {
                this.addDirections()
                 for (const [index, node] of this.parent.parent.value.nodes.entries()) {
                   if (node.type === 'submit-button' && !node.hasSubmitButton) continue
                   requestAnimationFrame( async () => {
                    await this.addHotspotNode(node, index, true)
                    if (node.position.top === 0 && node.position.left === 0 && node.position.width === 0 && node.position.height === 0) {
                      const data = { el: document.querySelector(`[data-key="${index}"][data-hotspot-instance="${this.uuid}"]`), uuid: this.uuid }
                      setTimeout(() => {
                        EventBus.$emit(`hotspot:dragged${this.uuid}`, data)
                      }, 300) // sadly this is neccessary due to the fact it does it quickly prior to the eventbus being set up
                    } else {
                      const data = { key: index, value: node, uuid: this.uuid }
                      this.validateDifference(data)
                    }
                  }, 500)
                }
                this.parentImgLoaded = true
                requestAnimationFrame(() => {
                  const debouncedRescale = _.debounce(() => {
                    this.rescaleNodes(img) 
                    this.testOverlapping(document.querySelector(`[data-key="0"][data-hotspot-instance="${this.uuid}"]`));
                  }, 600);
                  debouncedRescale();
                });
              }
            }
          }
        }
        // Ensure the button is added only once
        if (!document.querySelector(`#natural-size-button[data-uuid="${this.uuid}"]`) && this.isHotspot) {
          const naturalSizeButton = document.createElement('button')
          naturalSizeButton.classList.add('btn', 'btn-outline-secondary')
          naturalSizeButton.textContent = 'Expand image'
          naturalSizeButton.id = 'natural-size-button'
          naturalSizeButton.setAttribute('data-uuid', this.uuid)
          naturalSizeButton.addEventListener('click', () => {
            this.toggleImageSize(this.uuid)
          })
          el.appendChild(naturalSizeButton)
        }
      }

      const metadata = document.createElement('div')
      metadata.classList.add('mt-2')
      metadata.innerHTML = `<span>Filename:</span> <span class="text-muted">Fetching...</span>`
      this.preview.appendChild(metadata)

      // api call to get media metadata
      const regex = /\/(\d+)\//gm

      let source = null
      const matches = regex.exec(this.value)
      if (matches) {
        source = await store.dispatch('assets/getFile', { id: matches[1], useCache: true })
      }

      if (source) {
        metadata.innerHTML = `<span>Filename:</span> <strong>${source.name}</strong>`
      } else {
        metadata.innerHTML = `<span>Filename:</span> <span class="text-muted">${this.value}</span>`
      }
    } else {
      this.preview.innerHTML = ''
    }
  }
  
  attachAsset () {
    this.jsoneditor.options.assets[this.path] = {
      filename: this.value,
      options: this.schema.options
    }
  }
  
  removeAsset () {
    delete this.jsoneditor.options.assets[this.path]
  }
  
  onChange (bubble) {
    this.notify()
    this.watch_listener && this.watch_listener()
    bubble && this.change()

    if (this.value === null) {
      this.removeAsset()
    } else {
      this.attachAsset()
    }

    this.refreshPreview()
    $(this.clear).toggleClass('d-none', !this.value)
    $(this.replace).toggleClass('d-none', !this.value)
    $(this.button).toggleClass('d-none', !!this.value)
  }

  onResize (uuid, isRefresh = false) {
    if (!this.active) return
    this.parent?.parent?.value.nodes.forEach((node, index) => {
      const targetNode = document.querySelector(`[data-key="${index}"][data-hotspot-instance="${uuid}"]`)
      const parent = targetNode?.parentElement
      if (!parent) return // Ensure parent exists
      const img = parent.querySelector('img')
      const imgWidth = img.offsetWidth
      const imgHeight = img.offsetHeight
      setTimeout(() => {
        const correctedTop = imgHeight * (node.position.top / 100) // Calculate top in pixels
        const correctedLeft = imgWidth * (node.position.left / 100) // Calculate left in pixels

        // Apply the corrected top and left values
        gsap.to(targetNode, {
          x: correctedLeft, // Use correctedLeft for horizontal movement
          y: correctedTop // Use correctedTop for vertical movement
        })
        if (node.position.width) {
          targetNode.style.width = `${imgWidth * (node.position.width / 100)}px`
          targetNode.style.height = `${imgHeight * (node.position.height / 100)}px`
        }
      }, 100)
    })
    if (isRefresh) {
      this.refreshValue()
    }
    this.addDirections()
  }

  addDirections () {
    const hasDirections = this.container.querySelector('#hotspot-directions')
    if (hasDirections) {
      return
    }
    const directions = document.createElement('p')
    directions.id = 'hotspot-directions'
    directions.classList.add('form-text')
    directions.classList.add('description-text')
    directions.style.marginBottom = '0rem'
    directions.innerHTML = `Click and drag to move a hotspot. ${this.hasWidth ? 'Hold shift to resize that specific node while hovering over it.' : ''}`
    this.preview.parentElement.prepend(directions)
  }

  addHotspotNode (node, index, isrefresh = false) {
    if (index !== this.uuid && !isrefresh) return

    if (!this.isHotspot) return
    const targetDiv = this.preview.querySelector('#asset-graphic-wrapper')
    if (targetDiv && targetDiv.getAttribute('data-hotspot-instance') !== this.hotspotInstance) {
      // index will be passed but need to make sure the third param is true so refresh value will still work if index doesnt match the hotspotinstance which may use uuid to prevent 1 to 1 or 0 to 0 node instances.
      // Find the img element inside the target div
      const img = targetDiv.querySelector('img')
      if (img) {
        // Create a new div to wrap the img
        // TODO the node.key/index/number shouldn't be like this // not sure why not
        const content = (Number(node.key) + 1) || (index + 1) || 1
        let title
        if (node.value?.$_label !== undefined) {
          title = `${node.value.$_label}`
        } else {
          title = `Node ${(Number(node.key) + 1) || (Number(index) + 1) || 1}`
        }
        const el = document.createElement('div')
        const widthScaled = img.width / img.naturalWidth
        const heightScaled = img.height / img.naturalHeight
        const newWidth = 50 * widthScaled
        const newHeight = 50 * heightScaled
        el.style.border = '3px solid red'
        el.style.position = 'absolute'
        el.style.width = `${newWidth}px`
        el.style.height = `${newHeight}px`
        el.style.textAlign = 'center'
        el.style.margin = 'auto'
        el.style.resize = 'both'
        el.style.display = 'flex' // Added for centering
        el.style.justifyContent = 'center' // Added for centering
        el.style.alignItems = 'center' // Added for centering
        el.style.zIndex = `${content}` // Ensure it is above the image
        if (this.isHotspot) {
          img.classList.add('is--hotspot-image')
        }
        el.setAttribute('title', title)
        el.setAttribute('data-key', node.key ? (node.key) : index || 0)
        el.setAttribute('data-hotspot-instance', this.uuid)
        targetDiv.setAttribute('data-hotspot-instance', this.hotspotInstance)
        this.parent?.parent?.container.setAttribute('data-hotspot-instance', this.uuid) // this is needed for the parent edtior type of object ie base hotspot explore
        this.parent?.parent?.parent?.container.setAttribute('data-hotspot-instance', this.uuid) // this is needed for the parent editor type of Array ie article with hotspots.
        el.textContent = content
        el.id = title
        targetDiv.insertBefore(el, img)

        let startX, startY, startWidth, startHeight
        const draggable = Draggable.create(el, {
          type: 'x,y',
          bounds: img,
          onPress: () => {
            this.isClicked = true
          },
          onRelease: () => {
            this.isClicked = false
          },
          onDragStart: () => {
            this.isDragging = true
          },
          onDragEnd: () => {
            const data = { el, uuid: this.uuid }
            EventBus.$emit(`hotspot:dragged${this.uuid}`, data)
            this.isDragging = false
            this.testOverlapping(el)
          },
          zIndexBoost: false
        })

        el.addEventListener('mousedown', (event) => {
          if (this.isResizing) {
            if (event.target === el) {
              event.preventDefault() // Prevent text selection
            }
            startX = event.clientX
            startY = event.clientY
            startWidth = parseInt(document.defaultView.getComputedStyle(el).width, 10)
            startHeight = parseInt(document.defaultView.getComputedStyle(el).height, 10)
            document.addEventListener('mousemove', onMouseMove)
            document.addEventListener('mouseup', onMouseUp)
          }
        })

        const onMouseMove = (event) => {
          event.preventDefault()
          const width = startWidth + (event.clientX - startX)
          const height = startHeight + (event.clientY - startY)
          const parentBoundingRect = el.parentElement.getBoundingClientRect()
          const elBoundingRect = el.getBoundingClientRect()
          // Clamp the width to ensure it doesn't exceed the parent's width
          const maxWidth = parentBoundingRect.width - (elBoundingRect.x - parentBoundingRect.x)
          const newWidth = Math.min(width, maxWidth)

          // Clamp the height to ensure it doesn't exceed the parent's height
          const maxHeight = parentBoundingRect.height - (elBoundingRect.y - parentBoundingRect.y)
          const newHeight = Math.min(height, maxHeight)

          // Apply the new dimensions
          el.style.width = `${Math.max(newWidth, 0)}px` // Ensure width is not negative
          el.style.height = `${Math.max(newHeight, 0)}px` // Ensure height is not negative
          this.testOverlapping(el)
        }

        const onMouseUp = () => {
          el.style.cursor = 'default'
          draggable.forEach(e => e.enable())
          EventBus.$emit(`hotspot:resized${this.uuid}`, { el, uuid: this.uuid })
          document.removeEventListener('mouseup', onMouseUp)
          document.removeEventListener('mousemove', onMouseMove)
        }

        this.draggables[el.getAttribute('data-key')] = draggable
        return this.preview
      }
    } else {
      console.warn('No targetDiv found for adding hotspot node', targetDiv.getAttribute('data-hotspot-instance'), this.hotspotInstance, node, index, this.uuid, this.isHotspot)
    }
  }
  addKeyListeners () {
    if (!this.active) return
    this.removeKeyListeners()
    document.addEventListener('keydown', (this._keyDown = this.onKeyDown.bind(this)))
    document.addEventListener('keyup', (this._keyUp = this.onKeyUp.bind(this)))
  }
  removeKeyListeners () {
    document.removeEventListener('keydown', this._keyDown)
    document.removeEventListener('keyup', this._keyUp)
    this._keyDown = null
    this._keyUp = null
  }
  onKeyDown (event) {
    if (!this.active) return
    if (event.shiftKey && !this.isDragging && !this.isClicked && this.hasWidth) {
      for (const [key, draggable] of Object.entries(this.draggables)) {
        draggable.forEach(e => {
          e.disable()
          e.target.style.cursor = 'nwse-resize'
        })
      }
      this.isResizing = true
    }
  }
  onKeyUp (event) {
    if (event.key === 'Shift') {
      for (const [key, draggable] of Object.entries(this.draggables)) {
        draggable.forEach(e => {
          e.enable()
          e.target.style.cursor = 'default'
        })
      }
      this.isResizing = false
    }
  }
  validateDifference (data, approved = false) {
    const { key, value, uuid } = data
    if (value.type === 'submit-button' && !approved) {
      this.customNodehandler(data, uuid)
      return
    }
    if (this.uuid !== uuid) return
    const node = document.querySelector(`[data-key="${key}"][data-hotspot-instance="${uuid}"]`) // this is to find the individual instance with the unique id
    if (node) {
      const transform = node.style.transform
      const parent = node.parentElement
      const img = parent.querySelector('img') // this is the element i always want to use
      const match = transform.match(/translate3d\(([^,]+)px, ([^,]+)px, [^,]+px\)/)
      const top = match[2]
      const left = match[1]
      const imgWidth = img.width
      const imgHeight = img.height
      const leftPercentage = (left / imgWidth) * 100
      const topPercentage = (top / imgHeight) * 100
      if (value.position.top !== topPercentage || value.position.left !== leftPercentage || value.position.width !== node.offsetWidth || value.position.height !== node.offsetHeight) {
        const correctedHeight = imgHeight * (value.position.top / 100)
        const correctedWidth = imgWidth * (value.position.left / 100)
        gsap.to(node, { x: correctedWidth, y: correctedHeight })
        if ('width' in value.position) {
          if (value.position.width === 0 || value.position.height === 0) {
            return
          }
          node.style.width = `${imgWidth * (value.position.width / 100)}px`
          node.style.height = `${imgHeight * (value.position.height / 100)}px`
        }
      }
    }
  }

  customNodehandler (data, uuid) {
    const { key, value } = data
    const node = document.querySelector(`[data-key="${key}"][data-hotspot-instance="${uuid}"]`) // this is to find the individual instance with the unique id
    if (node && !value.hasSubmitButton) {
      this.removeHotspotNode(data, uuid)
    } else if (!node && value.hasSubmitButton) {
      this.addHotspotNode(data, key, true) // if the node is not found and the value hasSubmitButton then add it
    } else {
      this.validateDifference({ key, value, uuid }, true) // if the node is found and the value hasSubmitButton then validate the difference
    }
  }

  removeHotspotNode (data, uuid) {
    const { key } = data
    const node = document.querySelector(`[data-key="${key}"][data-hotspot-instance="${uuid}"]`) // this is to find the individual instance with the unique id
    if (node) {
      const draggable = this.draggables[key]
      if (draggable) {
        draggable[0].kill() // because of the query selector it returns an array of draggable instances so need to access within the array ie the [0]
        delete this.draggables[key]
      }
      node.remove()
    }
    if (data.parent?.value){
      for (let i = 0; i < data.parent.value.length; i++) {
        this.validateDifference({ key: i, value: data.parent.value[i], uuid }) // Validate (ie move them to their new correct positions because the editor deletes the last node no matter what) the previous node after removal
      }
   }
  }

  rescaleNodes(img) {
    if (!this.hasWidth) {
      const targetNode = document.querySelector(`[data-key="0"][data-hotspot-instance="${this.uuid}"]`);
      if (!targetNode) return; // Prevent error if node is not found
      const widthScaled = img.width / img.naturalWidth;
      const heightScaled = img.height / img.naturalHeight;
      const newWidth = 50 * widthScaled;
      const newHeight = 50 * heightScaled;
      targetNode.style.width = `${newWidth}px`;
      targetNode.style.height = `${newHeight}px`;
    }
  }

  testOverlapping (el) {
    // Not sure if this is neccessary adding it for future development options
  // Check for overlaps among all draggables for this hotspot
    const draggables = document.querySelectorAll(`[data-key][data-hotspot-instance="${this.uuid}"]`)
    let anyOverlap = false
    for (let i = 0; i < draggables.length; i++) {
      for (let j = i + 1; j < draggables.length; j++) { // Compare each draggable with every other draggable
        const rect1 = draggables[i].getBoundingClientRect()
        const rect2 = draggables[j].getBoundingClientRect()
        const overlap = !(rect1.right < rect2.left ||
                        rect1.left > rect2.right ||
                        rect1.bottom < rect2.top ||
                        rect1.top > rect2.bottom)
        if (overlap) {
          anyOverlap = true
          break
        }
      }
      if (anyOverlap) break
    }
    // Show or remove the warning only once, based on any overlap
    const warningId = 'overlap-warning'
    let warning = document.getElementById(warningId)
    if (anyOverlap) {
      if (!warning) {
        warning = document.createElement('div')
        warning.classList.add('alert', 'alert-danger')
        warning.id = warningId
        warning.textContent = 'Hotspots are overlapping'
        // Attach to the top-level container for visibility
        if (el.parentElement && el.parentElement.parentElement) {
          el.parentElement.parentElement.appendChild(warning)
        } else {
          document.body.appendChild(warning)
        }
      }
    } else {
      if (warning) warning.remove()
    }
  }

  toggleImageSize (uuid) {
    this.naturalSize = !this.naturalSize
    const button = document.querySelector(`#natural-size-button[data-uuid="${uuid}"]`)
    const img = this.preview.querySelector('img')
    if (this.naturalSize) {
    // Set the image to its natural size
      img.style.height = 'unset' // Clear maxHeight
      img.style.maxHeight = 'unset' // Set a max height for the image
      button.textContent = 'Shrink Image'
    } else {
      // Shrink the image
      img.style.maxHeight = '325px' // Set a max height for the image
      img.style.height = '' // Set maxHeight
      button.textContent = 'Expand image'
    }
    this.onResize(uuid)
  }

  enable () {
    if (!this.always_disabled) {
      if (this.input) {
        this.input.disabled = false
      }
      super.enable()
    }
  }
  
  disable (always_disabled) {
    if (always_disabled) {
      this.always_disabled = true
    }
    if (this.input) {
      this.input.disabled = true
    }
    super.disable()
  }
  
  setValue (val) {
    if (this.value !== val) {
      this.input.value = this.value = val
      this.onChange()

      if (this.parent) {
        this.parent.onChildEditorChange(this)
      } else {
        this.jsoneditor.onChange()
      }
    }
  }
  
  destroy () {
    if (this.preview && this.preview.parentNode) {
      this.preview.parentNode.removeChild(this.preview)
    }
    if (this.title && this.title.parentNode) {
      this.title.parentNode.removeChild(this.title)
    }
    if (this.input && this.input.parentNode) {
      this.input.parentNode.removeChild(this.input)
    }

    EventBus.$off(`hotspot:added${this.uuid}`) // this will be the solution moving forward
    EventBus.$off(`hotspot:changed${this.uuid}`)
    EventBus.$off(`hotspot:removed${this.uuid}`)
    EventBus.$off(`hotspot:newActiveTab${this.uuid}`)
    EventBus.$off('assets:updated', this.refreshPreview)

    this.removeKeyListeners()
    super.destroy()
  }
  
  addLinks () {
    // do nothing
  }
}

export default AssetEditor
